// One open/close routine for every bottom sheet and modal.
//
// openSheet(el, opts) does, in order: clear stale swipe styles, unhide, add `is-open` to the
// element and its overlay, lock page scroll, register a layer (Escape + back button),
// attach swipe-to-close on phones and close-on-overlay-click. closeSheet(el) reverses it.
// The CSS stays per component (.action-sheet, .coll-detail-sheet, .bl-pub-modal, ...);
// what is shared is the behaviour.

import { lockScroll, unlockScroll } from './scroll-lock';
import { pushLayer, popLayer } from './layers';
import { addSwipeToClose } from './swipe';

export interface SheetOptions {
    /** Backdrop element or selector. Gets `is-open` too and closes the sheet on click. */
    overlay?: HTMLElement | string | null;
    /** Called after the sheet closed, whatever closed it. */
    onClose?: () => void;
    /** Swipe down to close on phones. Default true. */
    swipe?: boolean;
    /** Lock page scroll while open. Default true. */
    lock?: boolean;
    /** Clicking the overlay closes. Default true. */
    closeOnOverlay?: boolean;
    /** Back button closes the sheet. Default true. */
    history?: boolean;
    /** Component toggles visibility with the `hidden` attribute (display:none) rather than
     *  a transform: re-apply `hidden` once the close transition has run. Default: true when
     *  the element carried `hidden` at open time. */
    hideOnClose?: boolean;
    /** Move focus here on open (element or selector inside the sheet). */
    focus?: HTMLElement | string | null;
    /** Element to return focus to on close. */
    returnFocus?: HTMLElement | null;
}

interface OpenState {
    layerId: number;
    overlay: HTMLElement | null;
    opts: SheetOptions;
    hideOnClose: boolean;
    lockKey: string;
}

const open = new WeakMap<HTMLElement, OpenState>();
const overlayBound = new WeakSet<HTMLElement>();
// Overlays can be shared (one backdrop, several sheets): remember which sheets use each.
const sheetsByOverlay = new Map<HTMLElement, Set<HTMLElement>>();
let keySeq = 0;

function resolve(ref: HTMLElement | string | null | undefined, scope: ParentNode = document): HTMLElement | null {
    if (!ref) return null;
    if (typeof ref === 'string') return scope.querySelector<HTMLElement>(ref) ?? document.querySelector<HTMLElement>(ref);
    return ref;
}

function lockKeyFor(el: HTMLElement): string {
    if (!el.dataset.docLockKey) el.dataset.docLockKey = 'sheet-' + (++keySeq);
    return el.dataset.docLockKey;
}

function overlayOwner(overlay: HTMLElement): HTMLElement | null {
    const set = sheetsByOverlay.get(overlay);
    if (!set) return null;
    for (const s of set) if (open.has(s)) return s;
    return null;
}

export function isSheetOpen(el: HTMLElement | null): boolean {
    return !!el && open.has(el);
}

export function openSheet(el: HTMLElement | null, opts: SheetOptions = {}): void {
    if (!el || open.has(el)) return;
    const overlay = resolve(opts.overlay);
    const hideOnClose = opts.hideOnClose ?? el.hasAttribute('hidden');

    if (overlay) {
        let set = sheetsByOverlay.get(overlay);
        if (!set) sheetsByOverlay.set(overlay, (set = new Set()));
        set.add(el);
    }

    // Stale inline styles from an interrupted swipe would keep the sheet off-screen.
    el.style.removeProperty('transform');
    el.style.removeProperty('transition');
    el.hidden = false;
    if (overlay) overlay.hidden = false;
    // Let the browser lay out the unhidden element before the class lands, so the
    // transition runs from the closed position.
    void el.offsetHeight;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    el.inert = false; // closed sheets carry `inert` so their buttons can't take focus while aria-hidden
    if (overlay) overlay.classList.add('is-open');

    const lockKey = lockKeyFor(el);
    if (opts.lock !== false) lockScroll(lockKey);

    const layerId = pushLayer(() => closeSheet(el), { history: opts.history });
    open.set(el, { layerId, overlay, opts, hideOnClose, lockKey });

    if (opts.swipe !== false) addSwipeToClose(el, () => closeSheet(el));

    if (overlay && opts.closeOnOverlay !== false && !overlayBound.has(overlay)) {
        overlayBound.add(overlay);
        overlay.addEventListener('click', (e) => {
            if (e.target !== overlay) return;
            const owner = overlayOwner(overlay);
            if (owner) closeSheet(owner);
        });
    }

    const focusEl = resolve(opts.focus ?? null, el);
    if (focusEl) setTimeout(() => focusEl.focus({ preventScroll: true }), 60);
}

export function closeSheet(el: HTMLElement | null): void {
    if (!el) return;
    const st = open.get(el);
    if (!st) return;
    open.delete(el);

    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    el.inert = true;
    if (st.overlay && !overlayOwner(st.overlay)) st.overlay.classList.remove('is-open');
    if (st.opts.lock !== false) unlockScroll(st.lockKey);
    popLayer(st.layerId);

    if (st.hideOnClose) {
        setTimeout(() => {
            if (open.has(el)) return; // reopened during the transition
            el.hidden = true;
            if (st.overlay && !overlayOwner(st.overlay)) st.overlay.hidden = true;
        }, 300);
    }

    if (st.opts.returnFocus) {
        try { st.opts.returnFocus.focus({ preventScroll: true }); } catch { /* detached */ }
    }
    if (typeof st.opts.onClose === 'function') {
        try { st.opts.onClose(); } catch (err) { console.error(err); }
    }
}

/** Toggle helper for buttons wired with a single handler. */
export function toggleSheet(el: HTMLElement | null, opts: SheetOptions = {}): void {
    if (!el) return;
    if (open.has(el)) closeSheet(el); else openSheet(el, opts);
}
